import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';

// Garde au niveau module : survit aux remounts (React StrictMode) pour ne consommer
// qu'une seule fois un token à usage unique par chargement de page.
const _processedTokens = new Set();

const ConfirmEmailChange = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');

  useEffect(() => {
    const confirm = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Lien invalide : token manquant.');
        return;
      }
      if (_processedTokens.has(token)) return; // déjà traité sur cette page (anti double-appel)
      _processedTokens.add(token);
      try {
        const res = await fetch(`${backendUrl}/api/auth/confirm-email-change`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setStatus('success');
          setNewEmail(data.email || '');
          setMessage(data.message || 'Votre nouvelle adresse email est désormais active.');
        } else {
          setStatus('error');
          setMessage(data.detail || 'Ce lien de confirmation est invalide ou a expiré.');
        }
      } catch (e) {
        setStatus('error');
        setMessage('Une erreur est survenue. Veuillez réessayer.');
      }
    };
    confirm();
  }, [token, backendUrl]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="confirm-email-change-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-100 p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="mx-auto text-cyan-500 animate-spin" size={48} />
            <h1 className="mt-4 text-xl font-bold text-slate-900">Confirmation en cours…</h1>
            <p className="mt-2 text-sm text-slate-500">Merci de patienter.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-500" size={48} data-testid="confirm-email-success" />
            <h1 className="mt-4 text-xl font-bold text-slate-900">Adresse email confirmée</h1>
            <p className="mt-2 text-sm text-slate-600">{message}</p>
            {newEmail && (
              <div className="mt-4 inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 rounded-lg px-4 py-2 text-sm font-medium">
                <Mail size={16} /> {newEmail}
              </div>
            )}
            <Link to="/login" className="mt-6 inline-block bg-cyan-500 hover:bg-cyan-600 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors" data-testid="confirm-email-login-link">
              Se connecter
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="mx-auto text-red-500" size={48} data-testid="confirm-email-error" />
            <h1 className="mt-4 text-xl font-bold text-slate-900">Confirmation impossible</h1>
            <p className="mt-2 text-sm text-slate-600">{message}</p>
            <Link to="/login" className="mt-6 inline-block bg-slate-800 hover:bg-slate-900 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors">
              Retour à la connexion
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default ConfirmEmailChange;
